import React from 'react';
import { Calendar, ChevronDown, ChevronRight, Loader2, Cpu, Sun } from 'lucide-react';
import { useGetSiteTaskQuery, type SiteTask } from '../../redux/api/tasksApi';
import { PRIORITY_BADGE_STYLES, STATUS_BADGE_STYLES, componentChipClass } from '../../constants/eventStyles';
import { timeAgo } from '../../utils/formatUtils';
import TaskDetailContent from './TaskDetailContent';

interface TaskCardProps {
  task: SiteTask;
  siteId: string;
  isExpanded: boolean;
  onToggle: () => void;
  onEventClick: (eventId: string) => void;
  onComponentClick: (component: string) => void;
  components?: string[];
  activeComponent: string;
  eventDate?: string;
  connectedPowerKw?: number | null;
}

function TaskCard({ task, siteId, isExpanded, onToggle, onEventClick, onComponentClick, components, activeComponent, eventDate, connectedPowerKw }: TaskCardProps) {
  const { data: detail, isLoading: detailLoading } = useGetSiteTaskQuery(
    { siteId, taskId: task.id },
    { skip: !isExpanded }
  );

  const priority = PRIORITY_BADGE_STYLES[task.priority] || PRIORITY_BADGE_STYLES[3];

  return (
    <div>
      <div
        className={`eg-card-elevated transition-colors ${
          isExpanded
            ? 'bg-[#17c480]/5 border-l-2 border-l-[#17c480]/30'
            : 'hover:bg-[var(--color-surface-hover)]'
        }`}
      >
        <button
          type="button"
          onClick={onToggle}
          className="w-full text-left p-3 cursor-pointer"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-[var(--color-text-primary)] truncate">{task.title}</span>
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${STATUS_BADGE_STYLES[task.status] || ''}`}>
                  {task.status.replace('_', ' ')}
                </span>
                <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${priority.cls}`}>
                  {priority.label}
                </span>
              </div>

              {task.description && (
                <p className="text-xs text-[var(--color-text-tertiary)] mt-1 line-clamp-2">
                  {task.description}
                </p>
              )}

              <div className="flex items-center gap-3 mt-2 text-[11px] text-[var(--color-text-muted)] flex-wrap">
                <span title={new Date(eventDate || task.created_at).toLocaleString()}>
                  {eventDate ? timeAgo(eventDate) : timeAgo(task.created_at)}
                </span>
                <span className="px-1.5 py-0.5 rounded border border-[var(--color-border)] text-[var(--color-text-secondary)]">
                  {task.task_type}
                </span>
                {task.due_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    Due: {new Date(task.due_date).toLocaleDateString()}
                  </span>
                )}
                {task.auto_created && (
                  <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]">
                    Auto-created
                  </span>
                )}
                {task.source_event_id && (
                  <span className="px-1.5 py-0.5 rounded bg-[var(--color-bg-tertiary)] text-[var(--color-text-muted)]">
                    From event
                  </span>
                )}
                {connectedPowerKw != null && (
                  <span className="flex items-center gap-1 text-[#17c480]">
                    <Sun className="w-3 h-3" />
                    {connectedPowerKw.toLocaleString(undefined, { maximumFractionDigits: 1 })} kW
                  </span>
                )}
              </div>
            </div>

            {isExpanded
              ? <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)] shrink-0 mt-1" />
              : <ChevronRight className="w-4 h-4 text-[var(--color-text-muted)] shrink-0 mt-1" />
            }
          </div>
        </button>

        {components && components.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 px-3 pb-2.5 -mt-0.5">
            <Cpu className="w-3 h-3 text-[var(--color-text-muted)] shrink-0" />
            {components.map((comp) => (
              <button
                key={comp}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onComponentClick(comp);
                }}
                className={componentChipClass(activeComponent === comp)}
                title={`Filter by ${comp}`}
              >
                {comp}
              </button>
            ))}
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="ml-4 mt-1 border-l-2 border-[#17c480]/20 pl-4 pb-2">
          {detailLoading ? (
            <div className="flex items-center gap-2 py-4 text-xs text-[var(--color-text-muted)]">
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading details...
            </div>
          ) : detail ? (
            <TaskDetailContent
              detail={detail}
              onEventClick={onEventClick}
              onComponentClick={onComponentClick}
              activeComponent={activeComponent}
            />
          ) : null}
        </div>
      )}
    </div>
  );
}

export default TaskCard;
